/**
 * ClinicSwitcher — toujours visible dans la navbar.
 *
 * - personnel plateforme (`is_platform_staff`) : liste TOUS les abonnés du
 *   système (control plane) avec un lien direct vers l'application de chacun
 *   (nouvel onglet), plus un accès SUPPORT par handoff.
 * - super_admin clinique (`can_switch_freely=true`, mode=`all`) : dropdown
 *   listant les cliniques accessibles de SA base.
 * - administrateur multi-org (`mode=memberships`) : dropdown de ses memberships.
 * - propriétaire du compte, module `account_wide_view` actif : entrée
 *   « Toutes les cliniques » en tête de la liste (ADR 0065).
 * - autres rôles (`can_switch_freely=false`) : badge lecture seule.
 */
import { useQuery } from "@tanstack/react-query";
import {
  Building2,
  Check,
  ChevronDown,
  ExternalLink,
  Layers,
  Loader2,
  Lock,
  LogIn,
} from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { ALL_CLINICS, useSwitchOrg } from "@/features/auth/hooks";
import { useAuthStore } from "@/features/auth/store";
import { platformApi, type Customer } from "@/features/platform/api";
import { apiErrorMessage } from "@/lib/api/errors";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAccessibleOrganisations } from "@/features/organisations/hooks";

const NAVBAR_BTN =
  "gap-2 border-primary-foreground/30 bg-transparent text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground";

/** Nom de la clinique dans la barre : icône seule sous sm, tronqué au-delà ;
 *  le nom complet est dans l'info-bulle (`title`) du déclencheur. */
const CLINIC_NAME =
  "hidden max-w-[8rem] truncate sm:inline md:max-w-[10rem] lg:hidden xl:inline xl:max-w-[9rem] 2xl:max-w-[12rem]";

export function ClinicSwitcher() {
  const me = useAuthStore((s) => s.me);
  // Le personnel éditeur GTS voit TOUJOURS la liste des abonnés (control plane),
  // même si son compte a des cliniques rattachées : il n'opère pas les cliniques
  // en place (console limitée aux 4 menus), il y accède par les liens d'app.
  if (me?.is_platform_staff) return <PlatformClinicSwitcher />;
  return <TenantClinicSwitcher />;
}

/** Liste tous les abonnés (control plane) avec, pour chacun, un lien direct
 *  vers son application (https://<slug>.<domaine principal>, ex.
 *  pandje.madagn.com) ouvert dans un nouvel onglet. Le bouton secondaire ouvre
 *  un accès support par handoff (session en tant qu'admin, marquée support). */
function PlatformClinicSwitcher() {
  const query = useQuery({
    queryKey: ["platform", "customers", "switcher"],
    queryFn: () => platformApi.list({ page_size: 100, ordering: "name" }),
    staleTime: 30_000,
  });
  const [busy, setBusy] = useState<string | null>(null);

  const customers = query.data?.results ?? [];

  const supportAccess = async (c: Customer) => {
    if (busy) return;
    setBusy(c.id);
    try {
      const { handoff_url } = await platformApi.supportAccess(c.id);
      window.location.href = handoff_url;
    } catch (e) {
      toast.error(apiErrorMessage(e, "Accès à l'espace impossible."));
      setBusy(null);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className={NAVBAR_BTN}>
          <Building2 className="h-4 w-4" />
          <span className="hidden max-w-[12rem] truncate text-sm sm:inline lg:hidden xl:inline xl:max-w-[9rem] 2xl:max-w-[12rem]">
            Applications des abonnés
          </span>
          <ChevronDown className="hidden h-4 w-4 sm:block" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-h-[70vh] w-80 overflow-y-auto">
        <DropdownMenuLabel>Tous les abonnés ({query.data?.count ?? 0})</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {query.isLoading && (
          <div className="flex justify-center px-2 py-4 text-muted">
            <Loader2 className="h-4 w-4 animate-spin" />
          </div>
        )}
        {!query.isLoading && customers.length === 0 && (
          <div className="px-2 py-3 text-xs text-muted">Aucun abonné.</div>
        )}
        {customers.map((c) => (
          <div key={c.id} className="flex items-stretch gap-1 px-1 py-0.5">
            <a
              href={c.app_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex min-w-0 flex-1 items-center justify-between gap-2 rounded-sm px-2 py-1.5 hover:bg-accent focus:bg-accent focus:outline-none"
            >
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">{c.name}</div>
                <div className="truncate font-mono text-[10px] lowercase text-muted">
                  {c.app_url.replace(/^https?:\/\//, "")}
                  {!c.is_active && " · suspendu"}
                </div>
              </div>
              <ExternalLink className="h-4 w-4 shrink-0 text-muted" />
            </a>
            <button
              type="button"
              onClick={() => supportAccess(c)}
              disabled={busy === c.id}
              title="Accès support (connexion en tant qu'admin)"
              aria-label={`Accès support ${c.name}`}
              className="flex shrink-0 items-center rounded-sm px-2 text-muted hover:bg-accent hover:text-foreground disabled:opacity-50"
            >
              {busy === c.id ? (
                <Loader2 className="h-4 w-4 animate-spin text-primary" />
              ) : (
                <LogIn className="h-4 w-4" />
              )}
            </button>
          </div>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/editeur/clients" className="text-sm">
            Gérer les abonnés →
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Switcher clinique classique (utilisateurs d'une base tenant). */
function TenantClinicSwitcher() {
  const switchOrg = useSwitchOrg();
  const query = useAccessibleOrganisations();

  if (query.isLoading) {
    return (
      <Button variant="outline" size="sm" disabled className={NAVBAR_BTN}>
        <Building2 className="h-4 w-4 animate-pulse" />
      </Button>
    );
  }

  if (!query.data) return null;

  const { mode, can_switch_freely, active_organisation_id, organisations } = query.data;
  const canSelectAll = query.data.can_select_all_clinics === true;
  const allClinics = canSelectAll && query.data.account_scope === true;
  const active = organisations.find((o) => o.id === active_organisation_id);

  // Mode lecture seule : juste un badge non cliquable
  if (!can_switch_freely) {
    if (!active) {
      return (
        <span className="inline-flex items-center gap-1.5 border border-warning/40 bg-warning/15 px-3 py-1.5 text-xs font-medium text-warning">
          <Lock className="h-3.5 w-3.5" />
          Non affilié
        </span>
      );
    }
    return (
      <span
        title={active.name}
        className="inline-flex min-w-0 items-center gap-2 border border-primary-foreground/30 bg-primary-foreground/5 px-2 py-1.5 text-sm text-primary-foreground sm:px-3"
      >
        <Building2 className="h-4 w-4 shrink-0" />
        <span className={CLINIC_NAME}>{active.name}</span>
        <Lock className="h-3 w-3 opacity-60" aria-label="Affiliation verrouillée" />
      </span>
    );
  }

  const switching = switchOrg.isPending;
  const labelPrefix = mode === "all" ? "Toutes les cliniques" : "Mes cliniques";
  const current = allClinics ? "Toutes les cliniques" : (active?.name ?? "Choisir une clinique");

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={switching}
          title={current}
          aria-label={`Clinique : ${current}`}
          className={`${NAVBAR_BTN} min-w-0 px-2 sm:px-3`}
        >
          <Building2 className="h-4 w-4 shrink-0" />
          <span className={`${CLINIC_NAME} text-sm`}>{current}</span>
          {/* Sous `sm`, l'icône seule suffit : la flèche faisait déborder 320 px. */}
          <ChevronDown className="hidden h-4 w-4 sm:block" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel>
          {labelPrefix} ({organisations.length})
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {organisations.length === 0 && (
          <div className="px-2 py-3 text-xs text-muted">Aucune clinique accessible.</div>
        )}
        {canSelectAll && (
          <>
            <DropdownMenuItem
              onSelect={() => {
                if (!allClinics) switchOrg.mutate(ALL_CLINICS);
              }}
              className="flex items-start justify-between gap-2"
            >
              <Layers className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">Toutes les cliniques</div>
                <div className="text-[11px] text-muted">
                  Assurances, bordereaux et factures assurées du compte
                </div>
              </div>
              {allClinics && <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        {organisations.map((o) => {
          const isActive = !allClinics && o.id === active_organisation_id;
          return (
            <DropdownMenuItem
              key={o.id}
              onSelect={() => {
                if (!isActive) switchOrg.mutate(o.id);
              }}
              className="flex items-start justify-between gap-2"
            >
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">{o.name}</div>
                <div className="font-mono text-[10px] uppercase text-muted">
                  {o.slug} · {o.patient_code_prefix}
                </div>
              </div>
              {isActive && <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
